/**
 * React Query Client Configuration
 *
 * Centralized QueryClient with optimized defaults for:
 * - Performance (caching, stale time)
 * - User experience (background refetching)
 * - Reliability (retry logic)
 * - Developer experience (logging)
 */

import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache Configuration
      staleTime: 1000 * 60 * 5, // 5 minutes - data is considered fresh for this duration
      gcTime: 1000 * 60 * 10, // 10 minutes - cached data kept in memory (formerly cacheTime)

      // Retry Configuration
      retry: 3, // Retry failed requests 3 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s, max 30s

      // Refetch Configuration
      refetchOnWindowFocus: false, // Don't refetch on window focus (can be overridden per query)
      refetchOnReconnect: true, // Refetch stale data when network reconnects
      refetchOnMount: true, // Refetch stale data when component mounts

      // Error Handling
      throwOnError: false, // Don't throw errors globally, handle in components
    },
    mutations: {
      // Error Handling
      throwOnError: false, // Handle errors in components
    },
  },
});

/**
 * Query Key Factory Pattern
 *
 * Centralized query keys for better cache management and invalidation.
 * Use these factories to generate consistent query keys across the app.
 *
 * Example:
 * ```typescript
 * import { queryKeys } from '@/lib/queryClient';
 *
 * useQuery({
 *   queryKey: queryKeys.credits.balance(),
 *   queryFn: getCredits,
 * });
 * ```
 */
export const queryKeys = {
  // Credits
  credits: {
    all: ["credits"] as const,
    balance: () => [...queryKeys.credits.all, "balance"] as const,
    history: (limit?: number, offset?: number) =>
      [...queryKeys.credits.all, "history", { limit, offset }] as const,
    check: (amount: number) =>
      [...queryKeys.credits.all, "check", amount] as const,
    checkAction: (sessionId?: string, actionType?: string) =>
      [
        ...queryKeys.credits.all,
        "check-action",
        {
          sessionId,
          actionType,
        },
      ] as const,
  },

  // Sessions
  sessions: {
    all: ["sessions"] as const,
    list: () => [...queryKeys.sessions.all, "list"] as const,
    detail: (id: string) => [...queryKeys.sessions.all, "detail", id] as const,
  },

  // Referrals
  referrals: {
    all: ["referrals"] as const,
    validate: (code: string) =>
      [...queryKeys.referrals.all, "validate", code] as const,
    generate: () => [...queryKeys.referrals.all, "generate"] as const,
  },

  // Services
  services: {
    all: ["services"] as const,
    identify: (caenCode: string) =>
      [...queryKeys.services.all, "identify", caenCode] as const,
    identifyBySession: (sessionId: string) =>
      [...queryKeys.services.all, "identify-session", sessionId] as const,
    lookup: () => [...queryKeys.services.all, "lookup"] as const,
  },

  // Sectors
  sectors: {
    all: ["sectors"] as const,
    list: (context?: string) =>
      context
        ? ([...queryKeys.sectors.all, "list", { context }] as const)
        : ([...queryKeys.sectors.all, "list"] as const),
  },

  // PDF
  pdf: {
    all: ["pdf"] as const,
    generate: (sessionId: string) =>
      [...queryKeys.pdf.all, "generate", sessionId] as const,
  },

  // Orders
  orders: {
    all: ["orders"] as const,
    checkStatus: (checkoutId: string) =>
      [...queryKeys.orders.all, "check-status", checkoutId] as const,
  },

  // Lookup
  lookup: {
    all: ["lookup"] as const,
    caen: (cui: string) => [...queryKeys.lookup.all, "caen", cui] as const,
    financial: (cui: string) =>
      [...queryKeys.lookup.all, "financial", cui] as const,
    sectors: () => [...queryKeys.lookup.all, "sectors"] as const,
  },

  // Payment Gateway
  paymentGateway: {
    all: ["paymentGateway"] as const,
    products: () => [...queryKeys.paymentGateway.all, "products"] as const,
    checkoutSession: (priceId: string) =>
      [...queryKeys.paymentGateway.all, "checkoutSession", priceId] as const,
    purchases: () => [...queryKeys.paymentGateway.all, "purchases"] as const,
  },

  // Risk Analysis
  riskAnalysis: {
    all: ["riskAnalysis"] as const,
    history: () => [...queryKeys.riskAnalysis.all, "history"] as const,
    bySession: (sessionId: string) =>
      [...queryKeys.riskAnalysis.all, "session", sessionId] as const,
    byId: (id: string) => [...queryKeys.riskAnalysis.all, "id", id] as const,
    bySector: (sessionId: string, sectorId: string) =>
      [
        ...queryKeys.riskAnalysis.all,
        "session",
        sessionId,
        "sector",
        sectorId,
      ] as const,
    matrixData: (sessionId: string, sectorId: string) =>
      [
        ...queryKeys.riskAnalysis.all,
        "matrix",
        "session",
        sessionId,
        "sector",
        sectorId,
      ] as const,
    defaults: (sessionId: string, sectorId: string) =>
      [
        ...queryKeys.riskAnalysis.all,
        "defaults",
        "session",
        sessionId,
        "sector",
        sectorId,
      ] as const,
  },
  // Entity Data
  entityData: {
    all: ["entityData"] as const,
    bySession: (sessionId: string) =>
      [...queryKeys.entityData.all, "session", sessionId] as const,
  },

  // Maturity Evaluation
  maturity: {
    all: ["maturity"] as const,
    framework: () => [...queryKeys.maturity.all, "framework"] as const,
    scoringAlgorithm: () =>
      [...queryKeys.maturity.all, "scoring-algorithm"] as const,
    questionnaire: (level: string) =>
      [...queryKeys.maturity.all, "questionnaire", level] as const,
    rubrics: (catCode: string) =>
      [...queryKeys.maturity.all, "rubrics", catCode] as const,
    evaluation: (sessionId: string) =>
      [...queryKeys.maturity.all, "evaluation", sessionId] as const,
  },

  // Company Preview
  companyPreview: {
    all: ["companyPreview"] as const,
    byCui: (cui: string) => [...queryKeys.companyPreview.all, cui] as const,
  },

  // Bilanț (api/financial-data)
  bilant: {
    all: ["bilant"] as const,
    byCui: (cui: string) => [...queryKeys.bilant.all, cui] as const,
  },

  // Sucursale UE (api/firma/sucursale)
  sucursaleUe: {
    all: ["sucursaleUe"] as const,
    byCui: (cui: string) => [...queryKeys.sucursaleUe.all, cui] as const,
  },

  // Targetare NIS2 (administrare)
  targetare: {
    all: ["targetare"] as const,
    stats: () => [...queryKeys.targetare.all, "stats"] as const,
    univers: (
      filtre: unknown,
      pagina: number,
      perPagina: number,
      versiune?: string | null
    ) =>
      [
        ...queryKeys.targetare.all,
        "univers",
        { filtre, pagina, perPagina, versiune: versiune ?? null },
      ] as const,
    firma: (cui: string) => [...queryKeys.targetare.all, "firma", cui] as const,
    contacteStats: () =>
      [...queryKeys.targetare.all, "contacte-stats"] as const,
    tinte: () => [...queryKeys.targetare.all, "tinte"] as const,
    activitate: () => [...queryKeys.targetare.all, "activitate"] as const,
    matrice: (versiune?: string | null) =>
      [...queryKeys.targetare.all, "matrice", versiune ?? null] as const,
    universComplet: (filtre: unknown, versiune?: string | null) =>
      [
        ...queryKeys.targetare.all,
        "univers-complet",
        { filtre, versiune: versiune ?? null },
      ] as const,
    mapare: () => [...queryKeys.targetare.all, "mapare"] as const,
    scoruri: () => [...queryKeys.targetare.all, "scoruri"] as const,
    liste: () => [...queryKeys.targetare.all, "liste"] as const,
    listaPropuneri: () =>
      [...queryKeys.targetare.all, "liste", "propuneri"] as const,
    listaVersiune: (id: number) =>
      [...queryKeys.targetare.all, "liste", "versiune", id] as const,
    firmeCriteriu: (criteriu: unknown, doarScop: boolean) =>
      [
        ...queryKeys.targetare.all,
        "firme-criteriu",
        { criteriu, doarScop },
      ] as const,
    cautaFirma: (q: string) =>
      [...queryKeys.targetare.all, "cauta-firma", q] as const,
    snapshoturi: () => [...queryKeys.targetare.all, "snapshoturi"] as const,
    snapshotDiferente: (id: number) =>
      [...queryKeys.targetare.all, "snapshoturi", id, "diferente"] as const,
    snapshotJob: (id: number) =>
      [...queryKeys.targetare.all, "snapshoturi", id, "job"] as const,
    setari: () => [...queryKeys.targetare.all, "setari"] as const,
    nomenclatoare: () => [...queryKeys.targetare.all, "nomenclatoare"] as const,
    termeneStats: () => [...queryKeys.targetare.all, "termene-stats"] as const,
  },

  // Form Sessions
  formSessions: {
    all: ["formSessions"] as const,
    byUser: (userId: string) =>
      [...queryKeys.formSessions.all, "user", userId] as const,
  },

  // Feature Flags
  featureFlags: {
    all: ["featureFlags"] as const,
    byName: (name: string) => [...queryKeys.featureFlags.all, name] as const,
  },

  // Decizie Inregistrare
  decizieInregistrare: {
    all: ["decizieInregistrare"] as const,
    bySession: (sessionId: string) =>
      [...queryKeys.decizieInregistrare.all, "session", sessionId] as const,
  },

  // Notificari de recalcul, istoricul evaluarilor si decizia de nivel de risc
  notificari: {
    all: ["notificari"] as const,
    recalcul: () => [...queryKeys.notificari.all, "recalcul"] as const,
  },

  evaluari: {
    all: ["evaluari"] as const,
    byCui: (cui: string) => [...queryKeys.evaluari.all, "cui", cui] as const,
  },

  decizieNivelRisc: {
    all: ["decizieNivelRisc"] as const,
    bySession: (sessionId: string) =>
      [...queryKeys.decizieNivelRisc.all, "session", sessionId] as const,
  },
} as const;
